import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { RadarAvatar, RadarCrest } from "../../components/agent-radar/RadarAtoms";
import { radarShortPosition } from "../../components/agent-radar/radarFormat";
import { clubRadarService } from "../../services/clubRadarService";
import { canUseClubRadar } from "../../utils/clubRoles";

// Club Radar — read-only shared-list snapshot (P3). Route
// /scouting/radar/snapshots/:id, opened from the club_radar_shortlist_share chat
// card. FROZEN at share time; NEVER carries notes or statuses (R5). Bare
// PrivateRoute; the BE scopes the read to owner-or-recipient.

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Snapshot-usability (Marins Findings 2026-08-21): rows link to the frozen
// subject's profile (player → /search/player, coach → /search/coach), the club
// chip links to the club profile when the freeze carried a platform_club_id,
// and the flag renders as an image. Rows shared before the columns existed
// carry null/'' and render exactly as before.
const snapshotProfilePath = (p) => {
  if (p.ts_coach_id) return `/search/coach/${p.ts_coach_id}`;
  const pid = p.ws_player_id || p.player_id;
  return pid ? `/search/player/${pid}` : null;
};

const SnapshotRow = ({ p, onNavigate }) => {
  const name = p.name || p.player_name || "Player";
  const clubName = p.club_name || p.club || "";
  const flag = p.nationality_flag || "";
  const line = [radarShortPosition(p.position), p.age].filter((v) => v !== null && v !== undefined && v !== "").join(" · ");
  const profilePath = snapshotProfilePath(p);
  const clubPath = p.platform_club_id ? `/search/club/${p.platform_club_id}` : null;
  return (
    <div className="flex items-center gap-[11px] p-[10px]">
      <RadarAvatar name={name} imageUrl={p.image_url || p.player_image_url} size={38} seed={p.initials || name} />
      <div className="flex-1 min-w-0">
        <div
          className={`text-[13.5px] font-bold text-[#111827] truncate ${profilePath ? "cursor-pointer hover:text-[#2283FF]" : ""}`}
          role={profilePath ? "link" : undefined}
          tabIndex={profilePath ? 0 : undefined}
          onClick={profilePath ? () => onNavigate(profilePath) : undefined}
          onKeyDown={profilePath ? (e) => { if (e.key === "Enter" || e.key === " ") onNavigate(profilePath); } : undefined}
        >
          {name}
        </div>
        <div className="flex items-center gap-1.5 mt-px min-w-0">
          {line && <div className="text-[11.5px] text-[#6B7280] truncate">{line}</div>}
          {flag && (
            <img
              src={flag}
              alt=""
              className="h-[11px] w-[15px] rounded-[2px] object-cover flex-shrink-0"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
        </div>
      </div>
      {clubName && (
        <div
          className={`flex items-center gap-1.5 min-w-0 ${clubPath ? "cursor-pointer" : ""}`}
          role={clubPath ? "link" : undefined}
          tabIndex={clubPath ? 0 : undefined}
          onClick={clubPath ? () => onNavigate(clubPath) : undefined}
          onKeyDown={clubPath ? (e) => { if (e.key === "Enter" || e.key === " ") onNavigate(clubPath); } : undefined}
        >
          <RadarCrest name={clubName} logoUrl={p.club_crest_url || p.club_logo_url} size={15} seed={clubName} />
          <div className={`text-[12px] truncate max-w-[120px] ${clubPath ? "text-[#2283FF] font-semibold" : "text-[#6B7280]"}`}>{clubName}</div>
        </div>
      )}
    </div>
  );
};

const ClubRadarSnapshot = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, denied: false, data: null });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { currentRole } = useSelector((s) => s.auth);
  const permissions = useSelector((s) => s.auth.permissions);
  // "Save entire list" (Marins Finding 2): club users WITH radar access pull
  // all rows onto their OWN club radar (My Radar; additive, BE re-gates).
  const canSave = canUseClubRadar(currentRole, permissions);

  const saveAll = async () => {
    if (saving || saved) return;
    setSaving(true);
    const res = await clubRadarService.saveSnapshotToRadar(id);
    setSaving(false);
    if (res.success) {
      setSaved(true);
      const n = res.data?.saved_count ?? 0;
      toast(n > 0 ? `Saved ${n} to your radar` : "Already on your radar", {
        position: "bottom-center",
        style: { background: "#111827", color: "#FFFFFF", fontSize: "12.5px", fontWeight: 600, borderRadius: "9px", padding: "9px 15px" },
      });
    } else {
      toast.error(res.error || "Couldn't save the list to your radar.");
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, denied: false, data: null });
    clubRadarService.getSnapshot(id, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.success) setState({ loading: false, denied: false, data: res.data });
        else setState({ loading: false, denied: true, data: null });
      })
      .catch(() => {});
    return () => controller.abort();
  }, [id]);

  const backToChat = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/inbox");
  };

  const { loading, denied, data } = state;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EDF0F4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2283FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (denied || !data) {
    return (
      <div className="min-h-screen bg-[#EDF0F4] flex flex-col items-center justify-center text-center px-6">
        <div className="text-[15px] font-bold text-[#111827]">Snapshot not available</div>
        <div className="text-[12.5px] text-[#6B7280] mt-2 max-w-[320px] leading-[1.55]">This shared list isn&rsquo;t available to you, or the link has expired.</div>
        <button type="button" onClick={backToChat} className="mt-4 bg-white border border-[#DDE3EB] rounded-[9px] px-[14px] py-2 text-[12.5px] font-semibold text-[#111827] hover:bg-[#F8FAFC]">‹ Back to chat</button>
      </div>
    );
  }

  const listName = data.list_name || data.name || "Shortlist";
  const players = Array.isArray(data.players) ? data.players : Array.isArray(data.entries) ? data.entries : [];
  const count = data.player_count ?? data.count ?? players.length;
  const sharedBy = data.shared_by_name;
  const sharedClub = data.shared_by_club;
  const sharedDate = formatDate(data.shared_at || data.created_at);

  return (
    <div className="min-h-screen bg-[#EDF0F4]">
      <div className="max-w-[680px] mx-auto px-4 pt-5 pb-14">
        <button type="button" onClick={backToChat} className="text-[#2283FF] text-[12.5px] font-semibold mt-0.5 mb-[14px] block">‹ Back to chat</button>

        <div className="bg-[#F0F6FF] border border-[#BFDBFE] rounded-[9px] px-[13px] py-[9px] text-[12px] text-[#1E4B8F] mb-[14px] leading-[1.5]">
          Read-only snapshot{sharedDate ? ` · shared ${sharedDate}` : ""} · won&rsquo;t update as the list changes · notes and statuses stay private.
        </div>

        <div className="flex items-baseline gap-[9px] mb-1 flex-wrap">
          <div className="text-[18px] font-bold text-[#111827] truncate">{listName}</div>
          <div className="text-[12px] text-[#9CA3AF] font-semibold whitespace-nowrap">{count} player{count === 1 ? "" : "s"}</div>
          {canSave && (
            <button
              type="button"
              onClick={saveAll}
              disabled={saving || saved}
              className={`ml-auto rounded-[9px] px-[14px] py-2 text-[12.5px] font-semibold ${
                saved
                  ? "bg-white border border-[#DDE3EB] text-[#16A34A] cursor-default"
                  : "bg-[#2283FF] text-white hover:bg-[#1B6FE0]"
              }`}
            >
              {saved ? "✓ Saved to your radar" : saving ? "Saving…" : "Save list to my Radar"}
            </button>
          )}
        </div>
        {(sharedBy || sharedClub) && (
          <div className="text-[12px] text-[#6B7280] mb-3">shared by {[sharedBy, sharedClub].filter(Boolean).join(" · ")}</div>
        )}

        <div className="bg-white border border-[#E4E9F0] rounded-[14px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-1.5">
          {players.map((p, i) => <SnapshotRow key={p.id ?? i} p={p} onNavigate={navigate} />)}
        </div>
      </div>
    </div>
  );
};

export default ClubRadarSnapshot;
